/**
 * fiko-yazi3d.ts — Parametrik 3D katı yazı: kullanıcı metin+font+boyut+kalınlık verir,
 * sistem extrude TextGeometry üretir (modelleme feature'ı olarak; CSG ile birleşebilir).
 * Fontlar lazy yüklenir + cache'lenir (rebuild senkron olduğundan önceden hazırlanır).
 */
import * as THREE from 'three';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { FontLoader, Font } from 'three/examples/jsm/loaders/FontLoader.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export const YAZI_FONTLARI: { id: string; ad: string; yol: string }[] = [
  { id: 'helvetiker', ad: 'Helvetiker', yol: '/js/three/fonts/helvetiker_regular.typeface.json' },
  { id: 'helvetiker_bold', ad: 'Helvetiker Kalın', yol: '/js/three/fonts/helvetiker_bold.typeface.json' },
  { id: 'optimer', ad: 'Optimer', yol: '/js/three/fonts/optimer_regular.typeface.json' },
  { id: 'optimer_bold', ad: 'Optimer Kalın', yol: '/js/three/fonts/optimer_bold.typeface.json' },
  { id: 'gentilis', ad: 'Gentilis', yol: '/js/three/fonts/gentilis_regular.typeface.json' },
  { id: 'gentilis_bold', ad: 'Gentilis Kalın', yol: '/js/three/fonts/gentilis_bold.typeface.json' },
  { id: 'droid_serif', ad: 'Droid Serif', yol: '/js/three/fonts/droid/droid_serif_regular.typeface.json' },
  { id: 'droid_serif_bold', ad: 'Droid Serif Kalın', yol: '/js/three/fonts/droid/droid_serif_bold.typeface.json' },
  { id: 'droid_sans', ad: 'Droid Sans', yol: '/js/three/fonts/droid/droid_sans_regular.typeface.json' },
  { id: 'droid_sans_bold', ad: 'Droid Sans Kalın', yol: '/js/three/fonts/droid/droid_sans_bold.typeface.json' },
  { id: 'droid_mono', ad: 'Droid Mono', yol: '/js/three/fonts/droid/droid_sans_mono_regular.typeface.json' },
];

const _cache = new Map<string, Font>();
const _yukleyici = new FontLoader();

export function fontHazirMi(id: string): boolean { return _cache.has(id); }

/** Yüklü fontu döndürür (CAM köprüsü font.generateShapes için). Yoksa helvetiker fallback. */
export function fontAl(id: string): Font | null { return _cache.get(id) || _cache.get('helvetiker') || null; }

export async function fontYukle(id: string): Promise<Font | null> {
  if (_cache.has(id)) return _cache.get(id)!;
  const t = YAZI_FONTLARI.find(f => f.id === id) || YAZI_FONTLARI[0];
  try {
    const font = await _yukleyici.loadAsync(t.yol);
    _cache.set(id, font);
    return font;
  } catch (e) { console.warn('[Yazı3D] font yüklenemedi', id, e); return null; }
}

/** Extrude 3D yazı geometrisi (font yüklü değilse null). harfBosluk>0 ise harf-harf üretip aralık ekler. */
export function yaziGeometri(metin: string, fontId: string, boyut = 10, kalinlik = 4, bevel = false, harfBosluk = 0): THREE.BufferGeometry | null {
  const font = _cache.get(fontId) || _cache.get('helvetiker');
  if (!font || !metin) return null;
  const ayar: any = { depth: Math.max(0.1, kalinlik), curveSegments: 8, bevelEnabled: bevel, bevelThickness: boyut * 0.04, bevelSize: boyut * 0.03, bevelSegments: 2 };

  let geo: THREE.BufferGeometry;
  if (!harfBosluk) {
    geo = new TextGeometry(metin, { font, size: boyut, ...ayar });
  } else {
    // HARF ARALIĞI: her karakteri ayrı extrude et, kümülatif konum + boşluk ile yerleştir, birleştir
    const parcalar: THREE.BufferGeometry[] = [];
    let x = 0;
    for (const ch of metin) {
      if (ch === ' ') { x += boyut * 0.45 + harfBosluk; continue; }
      const sekiller = (font as any).generateShapes(ch, boyut);
      if (!sekiller?.length) { x += boyut * 0.45 + harfBosluk; continue; }
      const g = new THREE.ExtrudeGeometry(sekiller, ayar);
      g.computeBoundingBox();
      const bb = g.boundingBox!; const w = bb.max.x - bb.min.x;
      g.translate(x - bb.min.x, 0, 0);
      parcalar.push(g);
      x += w + harfBosluk;
    }
    if (!parcalar.length) return null;
    geo = parcalar.length === 1 ? parcalar[0] : mergeGeometries(parcalar, false)!;
    if (parcalar.length > 1) for (const g of parcalar) g.dispose();
  }

  geo.computeBoundingBox();
  const bb = geo.boundingBox!;
  geo.translate(-(bb.max.x + bb.min.x) / 2, -(bb.max.y + bb.min.y) / 2, 0);  // X-Y ortala (+Z kalınlık → düzleme otur)
  geo.computeVertexNormals();
  return geo;
}
